import {
  defineComponent,
  inject,
  onBeforeUnmount,
  provide,
  ref,
  shallowRef,
  watchEffect,
  type InjectionKey,
  type Ref,
  type ShallowRef,
  type Slot,
  useSlots,
} from 'vue'

/** What the active tab hands up to its section layout's header. */
export interface SectionHeaderContext {
  /** The header's action slot as the active tab supplies it; absent when the tab has none. */
  actions: ShallowRef<Slot | undefined>
  /** The tab's segmented control or filter, pinned under the tab bar; absent when the tab has none. */
  pinned: ShallowRef<Slot | undefined>
  /** Whether the page leaves an empty run below the body for a document's section index. */
  scrollTail: Ref<boolean>
}

const SECTION_HEADER: InjectionKey<SectionHeaderContext> = Symbol('section-header')

/** Called by a section layout: creates the header context its tab bodies fill. */
export function provideSectionHeader(): SectionHeaderContext {
  const context: SectionHeaderContext = {
    actions: shallowRef(undefined),
    pinned: shallowRef(undefined),
    scrollTail: ref(false),
  }
  provide(SECTION_HEADER, context)
  return context
}

/** The header context of the enclosing section layout, or null outside one. */
export function useSectionHeader(): SectionHeaderContext | null {
  return inject(SECTION_HEADER, null)
}

/** Sets whether the enclosing section leaves a document scroll tail; cleared when the caller unmounts. */
export function useSectionScrollTail(wanted: () => boolean): void {
  const context = useSectionHeader()
  if (!context) return
  watchEffect(() => (context.scrollTail.value = wanted()))
  onBeforeUnmount(() => (context.scrollTail.value = false))
}

/** Defines a component that hands its default slot up to one part of the enclosing section layout's header and renders nothing itself. */
export function defineSectionHeaderPart(name: string, part: 'actions' | 'pinned') {
  return defineComponent({
    name,
    setup() {
      const context = useSectionHeader()
      const slot = useSlots().default

      if (context) context[part].value = slot

      onBeforeUnmount(() => {
        if (context && context[part].value === slot) context[part].value = undefined
      })

      return () => null
    },
  })
}
